import { useState } from "react";
import { useTheme } from "../ThemeProvider";
import { useDraggableModalPanel } from "../../hooks/useDraggableModalPanel";
import { sustituirPlaceholdersDocumentacion } from "../../utils/documentacionPlaceholders";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";

// ─── helpers ────────────────────────────────────────────────────────────────

const BACKEND_BASE =
  window.location.hostname === "localhost"
    ? "http://localhost:4000"
    : "https://vitalcasa-crm-production.up.railway.app";

function resolveImg(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : BACKEND_BASE + url;
}

function fmtPrecio(v, sufijo = " €") {
  if (!v) return null;
  return Number(v).toLocaleString("es-ES") + sufijo;
}

function fmtFecha(date = new Date()) {
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

/** Etiqueta de garaje/parking igual que en buildContextoPropiedadIA: «Parking» si consta
 *  abierto y no cerrado, «Garaje» en el resto de casos; interior/exterior como matiz. */
function garajeEtiquetaFicha(garajeTipos) {
  const keys = Array.isArray(garajeTipos)
    ? garajeTipos.map((x) => String(x).toLowerCase())
    : String(garajeTipos || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  const hasAbierto = keys.includes("abierto");
  const hasCerrado = keys.includes("cerrado");
  const hasCubierto = keys.includes("cubierto");
  const hasInterior = keys.includes("interior");
  const hasExterior = keys.includes("exterior");
  const base = hasAbierto && !hasCerrado ? "Parking" : "Garaje";
  const mods = [];
  if (hasCubierto) mods.push("cubierto");
  if (hasInterior) mods.push("interior");
  if (hasExterior) mods.push("exterior");
  if (mods.length) return `${base} ${mods.join(" y ")}`;
  if (hasCerrado) return `${base} cerrado`;
  return base;
}

/** Normaliza los campos de una propiedad independientemente de si viene de
 *  Propiedades.jsx (propsSeleccionadas) o de FichaCliente.jsx (props search).
 *  Para la Ficha Propiedad se fusiona antes con el detalle completo (ver
 *  handlePlantilla), de ahí los campos adicionales (estado, calefacción, etc.). */
function norm(p) {
  return {
    referencia: p.referencia || "—",
    tipo: p.tipo || p.tipo_nombre || "—",
    poblacion: p.poblacion || p.poblacion_nombre || "—",
    provincia: p.provincia || p.provincia_nombre || null,
    zona: p.zona || p.zona_nombre || null,
    operacion: p.operacion || "Venta",
    titulo: p.titulo || "Sin título",
    precio: p.precio_venta || p.precio || null,
    precio_propietario: p.precio_propietario || null,
    m2: p.m2_construidos || p.superficie_construida || null,
    habitaciones: p.num_dormitorios || null,
    banos: p.num_banos || null,
    // Planta real verificada = altura_planta (usada en toda la ficha para filtros/orientación
    // solar); `piso` es solo el descriptor postal de la dirección y puede no coincidir.
    planta: p.altura_planta != null && p.altura_planta !== ""
      ? p.altura_planta
      : (p.piso != null ? p.piso : (p.planta != null ? p.planta : null)),
    direccion: p.direccion_completa || null,
    propietario_nombre: (p.propietarios?.[0]?.nombre) || p.propietario || null,
    propietario_tel: p.propietarios?.[0]?.telefono || null,
    propietario_email: p.propietarios?.[0]?.email || null,
    recoger_llaves_en: p.recoger_llaves_en || null,
    num_llave: p.num_llave || null,
    tenemos_llaves: p.tenemos_llaves || false,
    info_visita: p.info_visita || null,
    agente_nombre: p.agente_nombre || null,
    foto: resolveImg(p.fotos?.[0]),
    fotos: (p.fotos || []).map(resolveImg),
    eq_piscina: p.eq_piscina_priv || p.piscina || false,
    eq_piscina_comun: p.eq_piscina_comun || false,
    eq_garaje: p.eq_garaje || false,
    eq_ascensor: p.eq_ascensor || false,
    eq_aire: p.eq_aire || false,
    amueblado: p.amueblado || false,
    primera_linea: p.ub_primera_linea || false,
    descripcion: p.descripcion_es || p.web_desc_corta_es || p.web_desc_larga_es || p.descripcion || null,
    // ── Campos adicionales para la Ficha Propiedad (requieren detalle completo) ──
    ano_construccion: p.ano_construccion || null,
    estado_nombre: p.estado_inmueble_nombre || null,
    calefaccion_nombre: p.calefaccion_tipo_nombre || null,
    eq_calefaccion: p.eq_calefaccion || false,
    gastos_comunidad: p.gastos_comunidad || null,
    cal_energetica: p.cal_energetica || null,
    eq_trastero: p.eq_trastero || false,
    eq_chimenea: p.eq_chimenea || false,
    eq_alarma: p.eq_alarma || false,
    eq_domotica: p.eq_domotica || false,
    eq_doble_cristal: p.eq_doble_cristal || false,
    armarios: p.armarios || false,
    num_terrazas: p.num_terrazas || p.num_terrazas_balcones || 0,
    num_balcones: p.num_balcones || 0,
    garaje_label: p.eq_garaje ? garajeEtiquetaFicha(p.garaje_tipos) : null,
  };
}

// ─── Inyectores de datos en los templates ────────────────────────────────────

function inyectarCitaAgente(doc, propiedades) {
  const agente = propiedades.find((p) => p.agente_nombre)?.agente_nombre || "";
  const fecha = fmtFecha();

  // Cabecera
  const headerDate = doc.querySelector(".header-date");
  if (headerDate) headerDate.textContent = `${fecha} · ${agente}`;

  // Limpiar lista de propiedades (5 hardcoded)
  const propsList = doc.querySelector("#props-list") || doc.querySelector(".props-list");
  if (!propsList) return;
  propsList.innerHTML = "";

  // Generar filas con datos reales
  propiedades.forEach((rawP, idx) => {
    const p = norm(rawP);
    const infoVisita = [
      p.tenemos_llaves && p.num_llave ? `Llave nº ${p.num_llave}` : null,
      p.recoger_llaves_en ? `Recoger en: ${p.recoger_llaves_en}` : null,
      p.info_visita || null,
    ].filter(Boolean).join(" · ") || "—";

    const esTipoAlquiler = (p.operacion || "").toLowerCase().includes("alquiler");
    const precioStr = p.precio
      ? `${fmtPrecio(p.precio)}${esTipoAlquiler ? "/mes" : ""}`
      : "—";
    const precioOwnerStr = p.precio_propietario ? fmtPrecio(p.precio_propietario) : null;

    const row = doc.createElement("div");
    row.className = "prop-row";
    row.innerHTML = `
      <div class="prop-num"><span>${idx + 1}</span></div>
      <div class="prop-photo" style="position:relative">
        ${p.foto
          ? `<img src="${p.foto}" alt="">`
          : `<div style="position:absolute;inset:0;background:#f0f0f0;display:flex;align-items:center;justify-content:center;color:#ccc;font-size:10px">Sin foto</div>`}
        <div class="op-badge">${p.operacion}</div>
      </div>
      <div class="prop-data">
        <div class="field"><div class="field-label">Referencia</div><div class="field-value">${p.referencia}</div></div>
        <div class="field"><div class="field-label">Tipo</div><div class="field-value">${p.tipo}</div></div>
        <div class="field"><div class="field-label">Población</div><div class="field-value">${p.poblacion}</div></div>
        <div class="field full"><div class="field-label">Dirección</div><div class="field-value">${p.direccion || p.titulo}</div></div>
        <div class="sep"></div>
        ${p.propietario_nombre ? `
          <div class="field two"><div class="field-label">Propietario</div><div class="field-value">${p.propietario_nombre}</div></div>
          <div class="field"><div class="field-label">Teléfono</div><div class="field-value">${p.propietario_tel || "—"}</div></div>
          ${p.propietario_email ? `<div class="field full"><div class="field-label">Email propietario</div><div class="field-value">${p.propietario_email}</div></div>` : ""}
          <div class="sep"></div>
        ` : ""}
        <div class="field full"><div class="field-label">Información visita</div><div class="field-value">${infoVisita}</div></div>
        <div class="sep"></div>
        <div class="field"><div class="field-label">${esTipoAlquiler ? "Precio alquiler" : "Precio venta"}</div><div class="field-value price">${precioStr}</div></div>
        ${precioOwnerStr ? `<div class="field"><div class="field-label">Precio propietario</div><div class="field-value price-owner">${precioOwnerStr}</div></div>` : ""}
      </div>
    `;
    propsList.appendChild(row);
  });

  // Footer
  if (agente) {
    const footerAgent = doc.querySelector(".footer-agent");
    if (footerAgent) footerAgent.innerHTML = `Agente: <strong>${agente}</strong>`;
  }
}

/** Filas fijas por hoja A4 en Resumen de Visitas (no escalan con el nº de props). */
const COMPARATIVA_POR_HOJA = 5;

function construirFilaComparativa(doc, rawP) {
  const p = norm(rawP);
  const esTipoAlquiler = (p.operacion || "").toLowerCase().includes("alquiler");
  const precioStr = p.precio
    ? `${Number(p.precio).toLocaleString("es-ES")} €${esTipoAlquiler ? "/mes" : ""}`
    : "";
  const m2Str = p.m2 ? `${Math.round(p.m2)}\u00a0<span class="spec-unit">m²</span>` : "";

  const tags = [
    p.eq_piscina && "Piscina",
    p.eq_piscina_comun && "Piscina com.",
    p.eq_garaje && "Garaje",
    p.eq_ascensor && "Ascensor",
    p.eq_aire && "Aire acond.",
    p.amueblado && "Amueblado",
    p.primera_linea && "1ª línea",
  ].filter(Boolean);

  const svgM2 = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="1"/><path d="M3 9h18M9 9v12"/></svg>`;
  const svgHab = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 20v-6a2 2 0 012-2h14a2 2 0 012 2v6"/><path d="M3 14V9a1 1 0 011-1h4v6"/><path d="M1 20h22"/></svg>`;
  const svgBan = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12h16v3a4 4 0 01-4 4H8a4 4 0 01-4-4v-3z"/><path d="M6 12V7a2 2 0 014 0"/></svg>`;

  const row = doc.createElement("div");
  row.className = "prop-row";
  row.innerHTML = `
    <div class="prop-photo" style="position:relative">
      ${p.foto
        ? `<img src="${p.foto}" alt="">`
        : `<div style="position:absolute;inset:0;background:#f0f0f0;display:flex;align-items:center;justify-content:center;color:#bbb;font-size:10px;border-radius:6px">Sin foto</div>`}
      <div class="op-badge">${p.operacion}</div>
      <div class="photo-ref">${p.referencia}</div>
    </div>
    <div class="prop-data">
      <div class="prop-ref">Ref. ${p.referencia}</div>
      <div class="prop-type">${p.tipo} · ${p.poblacion}</div>
      <div class="prop-title">${p.titulo}</div>
      <div class="specs">
        ${m2Str ? `<div class="spec">${svgM2}<span class="spec-val">${m2Str}</span></div>` : ""}
        ${p.habitaciones ? `<div class="spec">${svgHab}<span class="spec-val">${p.habitaciones}</span></div>` : ""}
        ${p.banos ? `<div class="spec">${svgBan}<span class="spec-val">${p.banos}</span></div>` : ""}
      </div>
      ${precioStr ? `<div class="prop-price">${precioStr}</div>` : ""}
      <div class="highlights">${tags.map((t) => `<span class="highlight-tag">${t}</span>`).join("")}</div>
    </div>
  `;
  return row;
}

function inyectarComparativa(doc, propiedades) {
  const agente = propiedades.find((p) => p.agente_nombre)?.agente_nombre || "";
  const fecha = fmtFecha();
  const cabeceraTxt = `${fecha} · ${agente}`;

  const originalA4 = doc.querySelector(".a4");
  if (!originalA4) return;
  const parent = originalA4.parentNode;
  const lista = Array.isArray(propiedades) ? propiedades : [];
  if (!lista.length) {
    const propsList = originalA4.querySelector(".props-list");
    if (propsList) propsList.innerHTML = "";
    const headerDate = originalA4.querySelector(".header-date");
    if (headerDate) headerDate.textContent = cabeceraTxt;
    return;
  }

  // Páginas de exactamente COMPARATIVA_POR_HOJA slots (altura fija por fila).
  for (let i = 0; i < lista.length; i += COMPARATIVA_POR_HOJA) {
    const chunk = lista.slice(i, i + COMPARATIVA_POR_HOJA);
    const a4 = i === 0 ? originalA4 : originalA4.cloneNode(true);
    if (i > 0) {
      const br = doc.createElement("div");
      br.style.cssText = "page-break-before:always;height:0;overflow:hidden";
      parent.appendChild(br);
      parent.appendChild(a4);
    }

    const headerDate = a4.querySelector(".header-date");
    if (headerDate) headerDate.textContent = cabeceraTxt;

    const propsList = a4.querySelector(".props-list");
    if (!propsList) continue;
    propsList.innerHTML = "";
    chunk.forEach((rawP) => propsList.appendChild(construirFilaComparativa(doc, rawP)));

    if (agente) {
      const footerAgent = a4.querySelector(".footer-agent");
      if (footerAgent) footerAgent.innerHTML = `Agente: <strong>${agente}</strong>`;
    }
  }
}

/** Color del distintivo de calificación energética por letra (escala real, no siempre verde). */
const CERT_ENERGETICO_COLOR = {
  A: "#2e7d32", B: "#66bb6a", C: "#c0d92e", D: "#fdd835",
  E: "#fb8c00", F: "#f4511e", G: "#c62828",
};

/** Rellena un .spec-item (o el .cert-row) con el valor real o lo oculta si no hay dato. */
function setSpecOrHide(a4, field, value) {
  const el = a4.querySelector(`[data-field="${field}"]`);
  if (!el) return;
  if (value == null || value === "") {
    el.classList.add("spec-hidden");
    return;
  }
  const valueEl = el.querySelector(".spec-value");
  if (valueEl) valueEl.textContent = value;
}

/** Texto de planta + ascensor. Solo afirma «con ascensor» si consta positivo:
 *  nunca se escribe «sin ascensor» de un dato que simplemente no se ha marcado.
 *  `altura_planta` suele ser numérica, pero admite texto libre (p. ej. «Ático»). */
function textoPlanta(p) {
  if (p.planta == null || p.planta === "") return null;
  const n = Number(p.planta);
  const txt = String(p.planta).trim();
  let base;
  if (!Number.isNaN(n)) base = n === 0 ? "Bajo" : `Planta ${n}ª`;
  else base = /^(planta|bajo|ático|atico)/i.test(txt) ? txt : `Planta ${txt}`;
  return p.eq_ascensor ? `${base} · con ascensor` : base;
}

function inyectarFichaPropiedad(doc, propiedades, ctx = {}) {

  const originalA4 = doc.querySelector(".a4");
  if (!originalA4) return;
  const parent = originalA4.parentNode;
  const usuarioActual = ctx.usuarioActual || null;

  // Generar una página A4 por propiedad
  propiedades.forEach((rawP, idx) => {
    const p = norm(rawP);
    const esTipoAlquiler = (p.operacion || "").toLowerCase().includes("alquiler");
    const precioStr = p.precio
      ? `${Number(p.precio).toLocaleString("es-ES")} €${esTipoAlquiler ? "/mes" : ""}`
      : null;

    // Clonar el template A4 original
    const a4 = originalA4.cloneNode(true);

    // Añadir salto de página antes de cada copia (excepto la primera)
    if (idx > 0) {
      const br = doc.createElement("div");
      br.style.cssText = "page-break-before:always;height:0;overflow:hidden";
      parent.appendChild(br);
    }

    // Referencia en cabecera
    const refVal = a4.querySelector(".header-ref-val");
    if (refVal) refVal.textContent = p.referencia;

    // Operación badge
    const opBadge = a4.querySelector(".op-badge");
    if (opBadge) opBadge.textContent = p.operacion;

    // Precio badge
    const priceBadge = a4.querySelector(".price-badge");
    if (priceBadge) {
      if (precioStr) priceBadge.textContent = precioStr;
      else priceBadge.style.display = "none";
    }

    // Fotos: si faltan fotos reales, se oculta el hueco en vez de dejar la imagen de
    // muestra de la plantilla (evita mostrar fotos de otra vivienda).
    const photoMainImg = a4.querySelector(".photo-main img");
    if (photoMainImg) {
      if (p.fotos[0]) photoMainImg.src = p.fotos[0];
      else a4.querySelector(".photo-main")?.style.setProperty("display", "none");
    }
    const extraFotos = p.fotos.slice(1, 4);
    const smallSlots = a4.querySelectorAll(".photo-small");
    smallSlots.forEach((slot, i) => {
      const url = extraFotos[i];
      if (url) {
        const img = slot.querySelector("img");
        if (img) img.src = url;
      } else {
        slot.style.display = "none";
      }
    });
    const photoGrid = a4.querySelector(".photo-grid");
    if (photoGrid) {
      if (!extraFotos.length) photoGrid.style.display = "none";
      // Reparte el ancho entre las fotos realmente disponibles (evita huecos en blanco).
      else photoGrid.style.gridTemplateColumns = `repeat(${extraFotos.length}, 1fr)`;
    }

    // Título
    const propTitle = a4.querySelector(".prop-title");
    if (propTitle) propTitle.textContent = p.titulo;

    // Ubicación general (sin dirección exacta: no se revela al no ir firmado el encargo)
    const propLoc = a4.querySelector(".prop-location");
    if (propLoc) {
      const lugar = [p.poblacion, p.provincia].filter((x) => x && x !== "—").join(", ");
      if (lugar) {
        const svg = propLoc.querySelector("svg");
        propLoc.textContent = "";
        if (svg) propLoc.appendChild(svg);
        propLoc.appendChild(doc.createTextNode(lugar));
      } else {
        propLoc.style.display = "none";
      }
    }

    // Specs: solo se muestran los campos con dato real de ESTA propiedad; el resto
    // se oculta en vez de dejar el valor de muestra de la plantilla.
    setSpecOrHide(a4, "superficie", p.m2 ? `${Math.round(p.m2)} m² construidos` : null);
    setSpecOrHide(a4, "habitaciones", p.habitaciones ? `${p.habitaciones} habitacion${p.habitaciones !== 1 ? "es" : ""}` : null);
    setSpecOrHide(a4, "banos", p.banos ? `${p.banos} baño${p.banos !== 1 ? "s" : ""}` : null);
    setSpecOrHide(a4, "planta", textoPlanta(p));
    setSpecOrHide(a4, "ano_construccion", p.ano_construccion || null);
    setSpecOrHide(a4, "estado", p.estado_nombre || null);
    setSpecOrHide(a4, "calefaccion", p.eq_calefaccion ? (p.calefaccion_nombre || "Sí") : null);
    setSpecOrHide(a4, "comunidad", p.gastos_comunidad ? `${Number(p.gastos_comunidad).toLocaleString("es-ES")} €/mes` : null);

    // Certificado energético: solo se muestra si hay letra A-G real (no se inventa).
    const certRow = a4.querySelector('[data-field="cert"]');
    if (certRow) {
      const letra = String(p.cal_energetica || "").trim().toUpperCase();
      if (/^[A-G]$/.test(letra)) {
        const badge = certRow.querySelector(".cert-badge");
        if (badge) {
          badge.textContent = letra;
          badge.style.background = CERT_ENERGETICO_COLOR[letra] || "#4caf50";
        }
      } else {
        certRow.classList.add("spec-hidden");
      }
    }

    // Descripción real de la propiedad (se oculta el bloque si no hay texto).
    const descBlock = a4.querySelector('[data-block="descripcion"]');
    if (descBlock) {
      if (p.descripcion) {
        const descText = descBlock.querySelector(".desc-text");
        if (descText) descText.textContent = p.descripcion;
      } else {
        descBlock.classList.add("block-hidden");
      }
    }

    // Puntos destacados: solo equipamiento realmente activo en esta propiedad.
    const extrasBlock = a4.querySelector('[data-block="extras"]');
    if (extrasBlock) {
      const tieneTerraza = Number(p.num_terrazas) > 0 || Number(p.num_balcones) > 0;
      const tags = [
        tieneTerraza && "Terraza",
        p.eq_garaje && p.garaje_label,
        p.eq_trastero && "Trastero",
        p.eq_ascensor && "Ascensor",
        p.eq_aire && "Aire acondicionado",
        p.eq_piscina && "Piscina privada",
        p.eq_piscina_comun && "Piscina comunitaria",
        p.armarios && "Armarios",
        p.eq_doble_cristal && "Doble cristal",
        p.eq_chimenea && "Chimenea",
        p.eq_domotica && "Domótica",
        p.eq_alarma && "Alarma",
        p.amueblado && "Amueblado",
        p.primera_linea && "1ª línea de playa",
      ].filter(Boolean);

      if (tags.length) {
        const grid = extrasBlock.querySelector(".extras-grid");
        if (grid) {
          grid.innerHTML = tags
            .map((t) => `<div class="extra-item"><div class="extra-dot"></div>${t}</div>`)
            .join("");
        }
      } else {
        extrasBlock.classList.add("block-hidden");
      }
    }

    // Pie: nombre y contacto de quien imprime la ficha (no del captador de la propiedad).
    const agentName = a4.querySelector(".agent-name");
    const agentRole = a4.querySelector(".agent-role");
    const agentAvatar = a4.querySelector(".agent-avatar");
    const nombreImpresor = usuarioActual?.nombre || p.agente_nombre || "VitalCasa";
    if (agentName) agentName.textContent = nombreImpresor;
    if (agentRole) agentRole.textContent = "VitalCasa";
    if (agentAvatar) {
      agentAvatar.textContent = nombreImpresor.split(" ").map((s) => s[0] || "").join("").substring(0, 2).toUpperCase();
    }
    const contactPhone = a4.querySelector(".contact-phone");
    const contactEmail = a4.querySelector(".contact-email");
    if (contactPhone) {
      if (usuarioActual?.telefono) contactPhone.textContent = usuarioActual.telefono;
      else contactPhone.style.display = "none";
    }
    if (contactEmail) {
      if (usuarioActual?.email) contactEmail.textContent = usuarioActual.email;
      else contactEmail.style.display = "none";
    }

    if (idx === 0) {
      // Sustituir el original
      parent.replaceChild(a4, originalA4);
    } else {
      parent.appendChild(a4);
    }
  });
}

function inyectarEscaparate(doc, propiedades) {

  const originalA3 = doc.querySelector(".a3");
  if (!originalA3) return;
  const parent = originalA3.parentNode;
  let lastInserted = null;

  propiedades.forEach((rawP, idx) => {
    const p = norm(rawP);
    const esTipoAlquiler = (p.operacion || "").toLowerCase().includes("alquiler");
    const precioStr = p.precio
      ? `${Number(p.precio).toLocaleString("es-ES")} €${esTipoAlquiler ? "/mes" : ""}`
      : "";

    const a3 = originalA3.cloneNode(true);

    // Foto principal
    const fotoImg = a3.querySelector(".foto img");
    if (fotoImg && p.foto) fotoImg.src = p.foto;

    // Operación badge
    const opBadge = a3.querySelector(".op-badge");
    if (opBadge) opBadge.textContent = p.operacion;

    // Referencia
    const refTag = a3.querySelector(".ref-tag");
    if (refTag) refTag.textContent = `Ref. ${p.referencia}`;

    // Tipo + Población
    const propType = a3.querySelector(".prop-type");
    if (propType) propType.textContent = `${p.tipo} · ${p.poblacion}`;

    // Título
    const propTitle = a3.querySelector(".prop-title");
    if (propTitle) propTitle.textContent = p.titulo;

    // Descripción
    const propDesc = a3.querySelector(".prop-desc");
    if (propDesc) {
      if (p.descripcion) {
        propDesc.textContent = p.descripcion;
      } else {
        propDesc.style.display = "none";
      }
    }

    // Precio
    const priceVal = a3.querySelector(".price-value");
    if (priceVal) {
      priceVal.textContent = precioStr || "";
      if (!precioStr) priceVal.style.display = "none";
    }

    // Specs por data-field
    const setSpec = (field, value) => {
      const el = a3.querySelector(`[data-field="${field}"] .spec-value, [data-field="${field}"] .spec-tag-value`);
      if (el) {
        if (value) el.textContent = value;
        else a3.querySelector(`[data-field="${field}"]`)?.remove();
      }
    };
    setSpec("superficie",   p.m2          ? `${Math.round(p.m2)} m\u00b2` : null);
    setSpec("habitaciones", p.habitaciones ? String(p.habitaciones) : null);
    setSpec("banos",        p.banos        ? String(p.banos) : null);
    setSpec("planta",       p.planta != null ? `${p.planta}\u00aa` : null);
    setSpec("garaje",       p.eq_garaje    ? "Incluido" : null);
    if (!p.eq_piscina && !p.eq_piscina_comun) a3.querySelector('[data-field="piscina"]')?.remove();

    if (idx === 0) {
      parent.replaceChild(a3, originalA3);
      lastInserted = a3;
    } else {
      const br = doc.createElement("div");
      br.style.cssText = "page-break-before:always;height:0;overflow:hidden";
      const after = lastInserted.nextSibling;
      if (after) {
        parent.insertBefore(br, after);
        parent.insertBefore(a3, br.nextSibling);
      } else {
        parent.appendChild(br);
        parent.appendChild(a3);
      }
      lastInserted = a3;
    }
  });
}

// ─── Función genérica: fetch → DOMParser → inyectar → abrir ventana ──────────

/** Abre el diálogo de impresión nativo usando un iframe oculto —
 *  no se abre ninguna pestaña ni ventana nueva.
 *  iframeW/iframeH: dimensiones del iframe (deben coincidir con el formato del template). */
async function abrirPlantillaConDatos(rutaTemplate, inyector, propiedades, extraPrintCss = "", iframeW = "210mm", iframeH = "297mm", ctx = {}) {
  const res = await fetch(rutaTemplate);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  let html = await res.text();
  html = await sustituirPlaceholdersDocumentacion(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  doc.querySelector(".screen-label")?.remove();
  doc.querySelector(".mode-switch")?.remove();

  inyector(doc, propiedades, ctx);

  // CSS común: eliminar padding/flex del body en impresión para evitar páginas extra
  const commonCss = `
    @media print {
      html, body {
        padding: 0 !important; margin: 0 !important;
        min-height: 0 !important; height: auto !important;
        display: block !important; background: white !important;
        overflow: visible !important;
      }
      img { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
    ${extraPrintCss}
  `;
  const styleEl = doc.createElement("style");
  styleEl.textContent = commonCss;
  doc.head.appendChild(styleEl);

  const htmlFinal = "<!DOCTYPE html>" + doc.documentElement.outerHTML;

  return new Promise((resolve, reject) => {
    // Reutilizar o crear el iframe oculto
    const FRAME_ID = "__vc_print_iframe";
    let iframe = document.getElementById(FRAME_ID);
    if (iframe) iframe.remove();

    iframe = document.createElement("iframe");
    iframe.id = FRAME_ID;
    const nProps = (propiedades && propiedades.length) || 1;
    const escaparate = rutaTemplate.includes("ficha_escaparate");
    const iframeHeight = escaparate
      ? `min-height:${297 * nProps}mm;height:auto`
      : `height:${iframeH}`;
    // Fuera de la pantalla: invisible, no ocupa espacio
    iframe.style.cssText =
      `position:fixed;left:-9999px;top:-9999px;width:${iframeW};${iframeHeight};` +
      "border:none;opacity:0;pointer-events:none;z-index:-1;";
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => { try { iframe.remove(); } catch (_) {} }, 1000);
      resolve();
    };

    try {
      const frameDoc = iframe.contentDocument || iframe.contentWindow.document;
      frameDoc.open("text/html", "replace");
      frameDoc.write(htmlFinal);
      frameDoc.close();

      const frameWin = iframe.contentWindow;

      // Esperar a que el documento (incluidas imágenes y fuentes) cargue
      frameWin.addEventListener("load", () => {
        frameWin.addEventListener("afterprint", cleanup, { once: true });
        // Fallback por si afterprint no dispara (algunos navegadores)
        setTimeout(cleanup, 300_000);
        setTimeout(() => {
          try { frameWin.focus(); frameWin.print(); } catch (_) { cleanup(); }
        }, 350);
      }, { once: true });
    } catch (err) {
      iframe.remove();
      reject(err);
    }
  });
}

// ─── Definición de plantillas ────────────────────────────────────────────────

const PLANTILLAS = [
  {
    id: "ficha_cita_agente",
    emoji: "📋",
    label: "Hoja de Cita",
    formato: "DIN A4",
    desc: "Datos internos por propiedad: dirección, propietario, info visita y precios",
    color: "#16a34a",
    archivo: "/plantillas/ficha_cita_agente_a4.html",
    inyector: inyectarCitaAgente,
    printCss: "@page { size: A4 portrait; margin: 0; }",
  },
  {
    id: "ficha_comparativa",
    emoji: "📊",
    label: "Resumen de Visitas",
    formato: "DIN A4",
    desc: "Presentación visual para el cliente con foto, specs y precio (5 propiedades por hoja)",
    color: "#7c3aed",
    archivo: "/plantillas/ficha_comparativa_a4.html",
    inyector: inyectarComparativa,
    printCss: `
      @page { size: A4 portrait; margin: 0; }
      @media print {
        .a4 {
          height: 297mm !important;
          max-height: 297mm !important;
          overflow: hidden !important;
          page-break-after: always;
        }
        .a4:last-of-type { page-break-after: auto; }
      }`,
  },
  {
    id: "ficha_propiedad",
    emoji: "🏠",
    label: "Ficha Propiedad",
    formato: "DIN A4",
    desc: "Una página por propiedad con fotos, características técnicas y datos del agente",
    color: "#c8002a",
    archivo: "/plantillas/ficha_propiedad_a4.html",
    inyector: inyectarFichaPropiedad,
    // overflow:hidden fuerza que todo quepa en exactamente 1 página A4
    printCss: `
      @page { size: A4 portrait; margin: 0; }
      @media print {
        .a4 {
          height: 297mm !important;
          max-height: 297mm !important;
          overflow: hidden !important;
          page-break-after: always;
        }
      }`,
  },
  {
    id: "ficha_escaparate",
    emoji: "🏷️",
    label: "Cartel Escaparate",
    formato: "DIN A3",
    desc: "Póster a gran formato para escaparate: foto, título, specs y precio",
    color: "#2563eb",
    archivo: "/plantillas/ficha_escaparate_a3.html",
    inyector: inyectarEscaparate,
    // El iframe debe renderizar el A3 completo (420mm × 297mm)
    iframeW: "420mm",
    iframeH: "297mm",
    // Una página A3 por cartel; varias propiedades = varias páginas (evita .a3 fixed superpuestos).
    printCss: `
      @page { size: 420mm 297mm; margin: 0; }
      @media print {
        html, body {
          background: #1a1a24 !important; margin: 0 !important; padding: 0 !important;
          display: block !important; overflow: visible !important;
        }
        .a3 {
          position: relative !important;
          top: auto !important; left: auto !important; right: auto !important; bottom: auto !important;
          width: 420mm !important;
          height: 297mm !important;
          background: transparent !important;
          box-shadow: none !important;
          overflow: hidden !important;
          transform: none !important;
          page-break-after: always !important;
          break-after: page !important;
        }
        .a3:last-of-type {
          page-break-after: auto !important;
          break-after: auto !important;
        }
        .foto {
          position: absolute !important;
          top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
          z-index: 0 !important;
        }
        .foto img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }
        .foto::after {
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }
        .op-badge {
          top: 8mm !important;
          left: 8mm !important;
        }
        .bottom-panel, .op-badge, .ref-tag {
          z-index: 2 !important;
          position: absolute !important;
        }
      }`,
  },
];

// ─── Listado básico (sin template, generado en JS) ───────────────────────────

function imprimirListadoBasico(propiedades, onClose) {
  const rows = (propiedades || [])
    .map((p) => {
      const n = norm(p);
      const precio = n.precio ? `${Number(n.precio).toLocaleString("es-ES")} €` : "—";
      return `<tr>
        <td>${n.referencia}</td>
        <td>${(p.titulo || "").replace(/</g, "&lt;")}</td>
        <td>${n.tipo}</td>
        <td>${n.poblacion}</td>
        <td style="text-align:right">${precio}</td>
      </tr>`;
    })
    .join("");

  const htmlFinal = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Listado · VitalCasa</title>
<style>
  body{font-family:Arial,sans-serif;padding:32px 40px;color:#1e293b;margin:0}
  h1{font-size:18px;font-weight:700;color:#c8002a;margin:0 0 3px}
  .sub{font-size:12px;color:#64748b;margin:0 0 22px}
  table{width:100%;border-collapse:collapse;font-size:13px}
  thead tr{background:#f8fafc}
  th{padding:9px 8px;border:1px solid #e2e8f0;text-align:left;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:.04em}
  th:last-child{text-align:right}
  td{padding:8px;border:1px solid #e2e8f0}
  td:last-child{text-align:right}
  tr:nth-child(even) td{background:#f8fafc}
  @media print{@page{margin:1.5cm} body{padding:0}}
</style></head>
<body>
<h1>VitalCasa · Propiedades seleccionadas</h1>
<p class="sub">${(propiedades || []).length} propiedad${(propiedades || []).length !== 1 ? "es" : ""} · ${fmtFecha()}</p>
<table>
  <thead><tr><th>Ref.</th><th>Título</th><th>Tipo</th><th>Localidad</th><th>Precio</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
</body></html>`;

  // Mismo iframe oculto — sin pestaña nueva
  const FRAME_ID = "__vc_print_iframe";
  let iframe = document.getElementById(FRAME_ID);
  if (iframe) iframe.remove();
  iframe = document.createElement("iframe");
  iframe.id = FRAME_ID;
  iframe.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:210mm;height:297mm;border:none;opacity:0;pointer-events:none;z-index:-1;";
  document.body.appendChild(iframe);

  const cleanup = () => { setTimeout(() => { try { iframe.remove(); } catch (_) {} }, 1000); onClose?.(); };

  const frameDoc = iframe.contentDocument || iframe.contentWindow.document;
  frameDoc.open("text/html", "replace");
  frameDoc.write(htmlFinal);
  frameDoc.close();
  iframe.contentWindow.addEventListener("load", () => {
    iframe.contentWindow.addEventListener("afterprint", cleanup, { once: true });
    setTimeout(cleanup, 300_000);
    setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (_) { cleanup(); } }, 350);
  }, { once: true });
}

// ─── Componente ──────────────────────────────────────────────────────────────

export default function ModalImprimirPropiedades({ isOpen, onClose, propiedades }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();
  const { usuario } = useAuth();
  const [cargando, setCargando] = useState(null);

  if (!isOpen) return null;

  const lista = propiedades || [];
  const agenteStr = lista.find((p) => p.agente_nombre)?.agente_nombre || "";

  const handlePlantilla = async (plantilla) => {
    setCargando(plantilla.id);
    try {
      // La Ficha Propiedad necesita datos completos (estado, calefacción, año de
      // construcción, etc.) que no vienen en los listados ligeros de otras pantallas.
      let datos = lista;
      if (plantilla.id === "ficha_propiedad") {
        datos = await Promise.all(
          lista.map(async (p) => {
            try {
              const res = await api.get(`/propiedades/${p.id}`);
              return { ...(res.data?.data || {}), ...p };
            } catch {
              return p;
            }
          })
        );
      }
      await abrirPlantillaConDatos(
        plantilla.archivo,
        plantilla.inyector,
        datos,
        plantilla.printCss || "",
        plantilla.iframeW || "210mm",
        plantilla.iframeH || "297mm",
        { usuarioActual: usuario }
      );
      onClose?.();
    } catch (err) {
      console.error("Error generando plantilla:", err);
      onClose?.();
    } finally {
      setCargando(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1100,
      }}
    >
      <div
        ref={dragRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 500, maxWidth: "95vw",
          background: theme.name === 'dark' ? c.bgCard : '#c8c8be', borderRadius: 16,
          border: `1px solid ${theme.name === 'dark' ? c.border : '#a8a8a0'}`, overflow: "hidden",
          boxShadow: theme.name === 'dark' ? 'none' : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
          ...panelPositionStyle,
        }}
      >
        {/* Header */}
        <div
          onMouseDown={startDrag}
          style={{
            padding: "14px 20px", cursor: "grab", userSelect: "none",
            borderBottom: `1px solid ${theme.name === 'dark' ? c.border : '#a8a8a0'}`,
            background: theme.name === 'dark' ? 'transparent' : '#e0e0d8',
            display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary }}>
              🖨️ Imprimir propiedades
            </h2>
            <p style={{ margin: "5px 0 0", fontSize: 12, color: c.textMuted }}>
              {lista.length} propiedad{lista.length !== 1 ? "es" : ""} · elige el formato y se abrirá el diálogo de impresión
            </p>
          </div>
          <button
            type="button" onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`,
              background: c.bgInput, color: c.textMuted, cursor: "pointer", fontSize: 15, flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Grid de plantillas */}
        <div style={{ padding: "16px 18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {PLANTILLAS.map((pl) => {
            const loading = cargando === pl.id;
            return (
              <button
                key={pl.id}
                type="button"
                disabled={!!cargando}
                onClick={() => handlePlantilla(pl)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6,
                  padding: "14px", borderRadius: 12,
                  border: `1.5px solid ${loading ? pl.color : c.border}`,
                  background: loading ? pl.color + "12" : c.bgInput,
                  cursor: cargando ? "wait" : "pointer",
                  textAlign: "left", fontFamily: "inherit", transition: "all 0.15s",
                  opacity: cargando && !loading ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!cargando) {
                    e.currentTarget.style.borderColor = pl.color;
                    e.currentTarget.style.background = pl.color + "12";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.borderColor = c.border;
                    e.currentTarget.style.background = c.bgInput;
                  }
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                  <span style={{ fontSize: loading ? 16 : 22 }}>{loading ? "⏳" : pl.emoji}</span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase",
                    color: pl.color, background: pl.color + "18", padding: "2px 7px", borderRadius: 4,
                  }}>{pl.formato}</span>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, marginBottom: 2 }}>
                    {loading ? "Generando..." : pl.label}
                  </div>
                  <div style={{ fontSize: 11, color: c.textMuted, lineHeight: 1.4 }}>{pl.desc}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Listado básico */}
        <div style={{ padding: "0 18px 18px" }}>
          <div style={{ borderTop: `1px solid ${c.border}`, paddingTop: 14 }}>
            <button
              type="button"
              disabled={!!cargando}
              onClick={() => imprimirListadoBasico(lista, onClose)}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%",
                padding: "11px 14px", borderRadius: 10, border: `1px solid ${c.border}`,
                background: "transparent", color: c.textSecondary, cursor: "pointer",
                fontSize: 13, fontWeight: 500, fontFamily: "inherit", textAlign: "left",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = c.bgInput; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ fontSize: 18 }}>📄</span>
              <span>
                <span style={{ display: "block", fontWeight: 600 }}>Listado básico</span>
                <span style={{ display: "block", fontSize: 11, opacity: 0.7, marginTop: 1 }}>
                  Tabla simple · se abre y lanza impresión automáticamente
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
